import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import * as casesApi from '../api';
import {firstNonEmptyOr} from '../../../core/helpers/nonEmpty';

// Case interface
export interface Case {
  caseId: string;
  workflowId: string;
  workflowName: string;
  createdDate: string;
  assignedPersona: string;
  assignee: string;
  status: string;
}

// Case Detail interface
export interface CaseDetail {
  id: string;
  caseId: string;
  workflowId: string;
  workflowName: string;
  workflowVersion: string;
  inputPayload: string;
  triggeredRuleCondition: string;
  assignedPersona: string;
  assignee: string;
  webhookUrl: string;
  status: string;
  comments: string | null;
  reviewedBy: string | null;
  createdTimestamp: string;
}

// Cases state interface
export interface CasesState {
  cases: Case[];
  currentCaseDetail: CaseDetail | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  filters: {
    sortBy: string;
    sortDirection: 'ASC' | 'DESC';
    search: string;
  };
}

const initialState: CasesState = {
  cases: [],
  currentCaseDetail: null,
  loading: false,
  error: null,
  pagination: {
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
    hasNext: false,
    hasPrevious: false,
  },
  filters: {
    sortBy: 'createdDate',
    sortDirection: 'DESC',
    search: '',
  },
};

// Async thunks
export const fetchCases = createAsyncThunk(
  'cases/fetchCases',
  async (params: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
    search?: string;
  }) => {
    const response = await casesApi.fetchCasesApi(
      params.page,
      params.size,
      params.sortBy,
      params.sortDirection,
      params.search
    );
    return response;
  }
);

export const fetchCaseById = createAsyncThunk(
  'cases/fetchCaseById',
  async (caseId: string) => {
    const response = await casesApi.fetchCaseByIdApi(caseId);
    return response.data;
  }
);

export const updateCaseStatus = createAsyncThunk(
  'cases/updateCaseStatus',
  async (params: {caseId: string; status: string; comment?: string}) => {
    const response = await casesApi.updateCaseStatusApi(
      params.caseId,
      params.status,
      params.comment
    );
    return {caseId: params.caseId, data: response};
  }
);

export const assignCase = createAsyncThunk(
  'cases/assignCase',
  async (params: {caseId: string; assignee: string}) => {
    const response = await casesApi.assignCaseApi(
      params.caseId,
      params.assignee
    );
    return {caseId: params.caseId, data: response};
  }
);

export const reTriggerWebhook = createAsyncThunk(
  'cases/reTriggerWebhook',
  async (caseId: string) => {
    const response = await casesApi.reTriggerWebhook(caseId);
    return {caseId, data: response};
  }
);

const casesSlice = createSlice({
  name: 'cases',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setFilters: (
      state,
      action: PayloadAction<{
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
        search?: string;
      }>
    ) => {
      state.filters = {...state.filters, ...action.payload};
    },
    setPagination: (
      state,
      action: PayloadAction<{
        page?: number;
        size?: number;
      }>
    ) => {
      state.pagination = {...state.pagination, ...action.payload};
    },
    clearCases: (state) => {
      state.cases = [];
      state.currentCaseDetail = null;
      state.pagination = initialState.pagination;
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cases
      .addCase(fetchCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCases.fulfilled, (state, action) => {
        state.loading = false;
        state.cases = action.payload.data.content;
        state.pagination = {
          page: action.payload.data.page,
          size: action.payload.data.size,
          totalElements: action.payload.data.totalElements,
          totalPages: action.payload.data.totalPages,
          first: action.payload.data.first,
          last: action.payload.data.last,
          hasNext: action.payload.data.hasNext,
          hasPrevious: action.payload.data.hasPrevious,
        };
      })
      .addCase(fetchCases.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to fetch cases',
          action.error.message
        );
      })

      // Fetch case by ID
      .addCase(fetchCaseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCaseById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCaseDetail = action.payload;
      })
      .addCase(fetchCaseById.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to fetch case',
          action.error.message
        );
      })

      // Update case status
      .addCase(updateCaseStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCaseStatus.fulfilled, (state, action) => {
        state.loading = false;
        // Update the specific case in the list
        const caseIndex = state.cases.findIndex(
          (c) => c.caseId === action.payload.caseId
        );

        if (caseIndex !== -1) {
          state.cases[caseIndex] = {
            ...state.cases[caseIndex],
            ...action.payload?.data?.data,
          };
        }

        if (
          state.currentCaseDetail &&
          state.currentCaseDetail.caseId === action.payload.caseId
        ) {
          state.currentCaseDetail = {
            ...state.currentCaseDetail,
            ...action.payload?.data?.data,
          };
        }
      })
      .addCase(updateCaseStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to update case status',
          action.error.message
        );
      })

      // Assign case
      .addCase(assignCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignCase.fulfilled, (state, action) => {
        state.loading = false;
        // Update the specific case in the list
        const caseIndex = state.cases.findIndex(
          (c) => c.caseId === action.payload.caseId
        );
        if (caseIndex !== -1) {
          state.cases[caseIndex] = {
            ...state.cases[caseIndex],
            ...action.payload.data,
          };
        }
      })

      .addCase(assignCase.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to assign case',
          action.error.message
        );
      })

      // Re-TriggerWebhook case
      .addCase(reTriggerWebhook.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reTriggerWebhook.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(reTriggerWebhook.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to assign case',
          action.error.message
        );
      });
  },
});

export const {clearError, setError, setFilters, setPagination, clearCases} =
  casesSlice.actions;

export default casesSlice.reducer;
